// Lógica pura de la dona "Distribución por categoría" (sin dependencias de
// React/Tremor para que sea testeable desde Node).
//
// Agrupa los productos de /api/comercial/ventas-por-producto por categoría de
// negocio y devuelve la lista ordenada EN ORDEN FIJO de negocio, con "Otros"
// siempre al final, y un color estable por categoría.
//
// Clasificación: usa el CAMPO "canal" del producto si viene (La 73 usa canales
// "BAR"/"Restaurante"/"Vinos y espumantes"), y si no, clasifica por prefijo del
// nombre del producto (Alado/Cosme usan "Alimentos - Fondos", etc.).

export interface ProductoInput {
  producto: string;
  canal?: string; // opcional; La 73 trae canal separado
  /** Categoría de negocio ya resuelta por el backend (comercial_producto_categoria).
   *  Si viene, se usa directamente (fuente canónica). */
  categoria?: string;
  /** Valor del MES seleccionado en el filtro (null = sin venta ese mes). */
  venta_mes?: number | null;
  /** Acumulado del año (venta_ytd) — usado cuando no hay mes filtrado. */
  venta_ytd?: number;
}

export interface CategoriaDato {
  name: string;
  value: number;
  color: string;
}

/** Orden de negocio: primero las categorías conocidas, "Otros" al final. */
export const CATEGORIA_ORDEN: string[] = [
  "Alimentos",
  "Bebidas Sin Alcohol",
  "Bebidas con Alcohol",
  "Vinos y Espumantes",
  "Productos Retail",
  "Otros",
];

/**
 * Colores de la paleta corporativa + tonos de apoyo, asignados de forma estable
 * por posición (no por nombre) para que la dona se vea uniforme entre tableros.
 */
export const CHART_PALETTE = [
  "#15335B", // primaryDark
  "#2E5B88", // primaryMedium
  "#6F91B3", // primaryLight
  "#E9560D", // naranja corporativo
  "#8B5E3C", // marrón
  "#4E79A7", // azul acero
];

const CATEGORIA_PREFIXES: [string, string][] = [
  ["Alimentos", "Alimentos - "],
  ["Bebidas Sin Alcohol", "Bebidas Sin Alcohol - "],
  ["Bebidas con Alcohol", "Bebidas con Alcohol - "],
  ["Vinos y Espumantes", "Vinos y Espumantes - "],
  ["Productos Retail", "Productos Retail - "],
];

/**
 * Mapeo de CANAL del restaurante → categoría de negocio. La 73 usa canales
 * propios ("BAR", "Restaurante", "Vinos y espumantes", ...) que no coinciden
 * con los prefijos de nombre de Alado/Cosme.
 */
const CANAL_TO_CATEGORIA: [string, string][] = [
  ["bar", "Bebidas con Alcohol"],
  ["restaurante", "Alimentos"],
  ["vinos y espumantes", "Vinos y Espumantes"],
  ["Vinos", "Vinos y Espumantes"],
  ["retail", "Productos Retail"],
];

/**
 * Clasifica un producto en su categoría de negocio. Prioriza el campo `canal`
 * (La 73); si no hay canal, usa el prefijo del nombre (Alado/Cosme). Normaliza
 * espacios y mayúsculas. Si no encaja → "Otros".
 */
export function getCategoria(producto: string, canal?: string): string {
  if (canal && canal.trim()) {
    const c = canal.replace(/\s+/g, " ").trim().toLowerCase();
    for (const [canalKey, catName] of CANAL_TO_CATEGORIA) {
      if (c.includes(canalKey.toLowerCase())) return catName;
    }
  }
  const p = producto.replace(/\s+/g, " ").trim().toLowerCase();
  for (const [cat, prefix] of CATEGORIA_PREFIXES) {
    if (p.startsWith(prefix.toLowerCase())) return cat;
  }
  return "Otros";
}

/**
 * Agrupa productos por categoría y ordena en ORDEN FIJO de negocio.
 * `modo` controla el valor usado:
 *   - "mes"  → usa venta_mes (el valor del mes filtrado). Productos sin dato ese
 *              mes (null/undefined) aportan 0.
 *   - "ytd"  → usa venta_ytd (acumulado del año).
 */
export function agruparPorCategoria(
  productos: ProductoInput[],
  modo: "mes" | "ytd" = "ytd"
): CategoriaDato[] {
  const sums = new Map<string, number>();
  for (const p of productos) {
    // Fuente canónica: si el backend ya trajo la categoría, usarla directamente.
    const cat = p.categoria && p.categoria.trim()
      ? p.categoria
      : getCategoria(p.producto, p.canal);
    const val = modo === "mes" ? (p.venta_mes ?? 0) : (p.venta_ytd ?? 0);
    sums.set(cat, (sums.get(cat) ?? 0) + val);
  }
  // Solo categorías con valor > 0 (en modo mes, si nada vendió ese mes → vacío).
  const orden = CATEGORIA_ORDEN.filter((c) => sums.has(c) && sums.get(c)! > 0);
  return orden.map((name, idx) => ({
    name,
    value: sums.get(name)!,
    color: CHART_PALETTE[idx % CHART_PALETTE.length],
  }));
}

/** Total de la suma de todas las categorías. */
export function totalCategorias(cat: CategoriaDato[]): number {
  return cat.reduce((s, c) => s + c.value, 0);
}

/** Monto compacto tipo "S/ 3.16M" / "S/ 426k" para la leyenda compacta. */
export function formatCompactMoney(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `S/ ${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `S/ ${(n / 1_000).toFixed(0)}k`;
  return `S/ ${n.toFixed(0)}`;
}
